import { useState, useEffect } from "react";
import { useStableCallback } from "@/hooks/useStableCallback";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { getScanSettings, saveScanSettings } from "@/lib/scanSettings";

const SOURCES = ["Flatbed", "Feeder"];
const COLORS = ["Color", "Grayscale", "BlackWhite"];
const DPIS = [100, 150, 200, 300, 600];

/**
 * CompactScannerPanel — Inline scanner controls: scanner, source, color, DPI.
 * Reads/writes to the same per-user localStorage as ScanSettingsPanel.
 */
export default function CompactScannerPanel({ onSettingsReady }) {
  const [scanners, setScanners] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const isElectron = !!window.electronAPI?.listScanners;
  const employee = JSON.parse(localStorage.getItem("employee") || "{}");
  const employeeId = employee.id;
  const notifyReady = useStableCallback((st) => { if (onSettingsReady) onSettingsReady(st); });

  const discover = async (fn) => {
    try {
      const res = await fn();
      if (res?.error) {
        toast.error(res.message || "Scanner discovery failed", { id: "scanner-discovery" });
        return;
      }
      const list = res?.scanners || [];
      setScanners(list);
      if (list.length === 0) {
        toast.info("No scanners found. Make sure your scanner is on and connected.", { id: "scanner-discovery" });
      } else if (!list.find(sc => sc.id === settings?.scannerId)) {
        update({ scannerId: list[0].id });
      } else {
        toast.success(`Found ${list.length} scanner${list.length === 1 ? "" : "s"}`, { id: "scanner-discovery" });
      }
    } catch (e) {
      toast.error(`Scanner error: ${e.message || "Unknown error"}`, { id: "scanner-discovery" });
    }
  };


  const handleRefresh = async () => {
    if (!window.electronAPI?.refreshScanners) return;
    setRefreshing(true);
    await discover(window.electronAPI.refreshScanners);
    setRefreshing(false);
  };

  const update = useStableCallback((patch, base) => {
    const next = { ...(base || settings), ...patch };
    setSettings(next);
    saveScanSettings(employee.id, next);
    if (onSettingsReady) onSettingsReady(next);
  });

  useEffect(() => {
    const s = getScanSettings(employeeId);
    setSettings(s);
    notifyReady(s);

    if (!isElectron) { setLoading(false); return; }

    (async () => {
      try {
        const res = await window.electronAPI.listScanners();
        if (res?.error) {
          toast.error(res.message || "Scanner discovery failed", { id: "scanner-discovery" });
          setLoading(false);
          return;
        }
        const list = res?.scanners || [];
        setScanners(list);
        if (list.length === 0) {
          toast.info("No scanners found. Make sure your scanner is on and connected.", { id: "scanner-discovery" });
        } else if (!list.find(sc => sc.id === s.scannerId)) {
          update({ scannerId: list[0].id }, s);
        }
      } catch (e) {
        toast.error(`Scanner error: ${e.message || "Unknown error"}`, { id: "scanner-discovery" });
      } finally { setLoading(false); }
    })();
  }, [employeeId, isElectron, notifyReady, update]);

  if (!isElectron) return null;
  if (loading) return <div className="text-[10px] text-zinc-400">Loading scanners...</div>;

  const sel = "h-6 text-[10px] bg-white border border-zinc-300 rounded px-1 focus:outline-none focus:ring-1 focus:ring-blue-400";

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="compact-scanner-panel">
      {/* Scanner */}
      <select
        className={`${sel} max-w-[160px] truncate`}
        value={settings?.scannerId || ""}
        onChange={(e) => update({ scannerId: e.target.value })}
        data-testid="compact-scanner-select"
      >
        {scanners.length === 0 && <option value="">No scanners</option>}
        {scanners.map(s => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>

      {/* Refresh — scanner not showing up? re-discover without a full app restart */}
      <button
        type="button"
        onClick={handleRefresh}
        disabled={refreshing}
        title="Refresh scanner list"
        data-testid="compact-scanner-refresh"
        className="h-6 w-6 flex items-center justify-center rounded border border-zinc-300 bg-white text-zinc-500 hover:text-zinc-800 hover:border-zinc-400 disabled:opacity-50 transition-colors"
      >
        <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
      </button>

      {/* Source */}
      <select
        className={sel}
        value={settings?.source || "Flatbed"}
        onChange={(e) => update({ source: e.target.value })}
        data-testid="compact-scanner-source"
      >
        {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>

      {/* Color */}
      <select
        className={sel}
        value={settings?.colorMode || "Color"}
        onChange={(e) => update({ colorMode: e.target.value })}
        data-testid="compact-scanner-color"
      >
        {COLORS.map(c => <option key={c} value={c}>{c === "BlackWhite" ? "B&W" : c}</option>)}
      </select>

      {/* DPI */}
      <select
        className={sel}
        value={settings?.dpi || 300}
        onChange={(e) => update({ dpi: parseInt(e.target.value) })}
        data-testid="compact-scanner-dpi"
      >
        {DPIS.map(d => <option key={d} value={d}>{d} DPI</option>)}
      </select>
    </div>
  );
}
